/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { IEveUserRolesData } from './eve-user-roles.data';
import { IEveUserRolesProps } from './eve-user-roles.props.autogenerated';
import { ICookieSetOptions } from '@msdyn365-commerce/core-internal/dist/types/interfaces/context/ICookieContext';
import { getUserLevelByUserIdAsync } from '../../actions/DataService/DataActionExtension.g';
import Msdyn365, { getCatalogId } from '@msdyn365-commerce/core';

export interface IEveUserRolesViewProps extends IEveUserRolesProps<IEveUserRolesData> {}

export enum UserRolePermissions {
    CanViewProducts,
    CanViewProductAvailability,
    CanViewProductPrice,
    CanViewProductPriceInCheckout,
    CanViewProductBasePrice,
    CanUseOrderTemplates,
    CanSubmitOrders,
    CanViewInvoices
}

/**
 *
 * EveUserRoles component
 * @extends {React.PureComponent<IEveUserRolesProps<IEveUserRolesData>>}
 */
class EveUserRoles extends React.PureComponent<IEveUserRolesProps<IEveUserRolesData>> {
    private static lvl1String: string = 'd709d47b-83c0-4334-83c7-a49878c00de5';
    private static lvl2String: string = '5e93d514-ce06-40c6-bd7e-29dc4e5f4705';
    private static lvl21String: string = '0197ebc7-fd10-7cb4-bdf3-3be72932593b';
    private static lvl3String: string = 'ac888c92-c80a-4bd5-bd41-e6d96cafc0ef';
    private static lvl4String: string = 'f9a1c2b3-4d56-7890-ab12-cde3456789ef';
    private static lvl41String: string = '7e8f9a0b-1c23-4567-89ab-cdef01234567';
    private static USER_COOKIE_NAME_CONST = 'ULID_CHECK';

    private static readonly _catalogIdQueryParamKey: string = 'catalogid';

    /**
     * Helper method to set one or multiple cookies for user role mod
     * @param setIdentifierCookie Boolean specifying whether the user id cookie should be set
     * @param setUserLevelCookie Boolean specifying whether the user level cookie should be set
     * @param userLevel Number specifying the level which the user should be set to
     */
    private setUserRoleCookie(setIdentifierCookie: boolean, setUserLevelCookie: boolean, userLevel?: number) {
        const maxAge = this.props.context.app.config.cookieMaxAge * 86400000;
        const expDate: Date = new Date(Date.now());
        expDate.setDate(expDate.getDate() + this.props.context.app.config.cookieMaxAge);
        const cookieOptions: ICookieSetOptions = { maxAge: maxAge, expires: expDate };

        if (setIdentifierCookie) {
            this.props.context.request.cookies.set(
                EveUserRoles.USER_COOKIE_NAME_CONST,
                this.props.context.request.user.isAuthenticated &&
                    //@ts-ignore We need the channelid from the user
                    this.props.context.request.User.ChannelId === this.props.context.request.channel.RecordId.toString()
                    ? this.props.context.request.user.customerAccountNumber
                    : 1,
                cookieOptions
            );
        }

        if (setUserLevelCookie) {
            let value = '';
            switch (userLevel) {
                case 1:
                    value = EveUserRoles.lvl1String;
                    break;
                case 2:
                    value = EveUserRoles.lvl2String;
                    break;
                case 21:
                    value = EveUserRoles.lvl21String;
                    break;
                case 3:
                    value = EveUserRoles.lvl3String;
                    break;
                case 4:
                    value = EveUserRoles.lvl4String;
                    break;
                case 41:
                    value = EveUserRoles.lvl41String;
                    break;
                default:
                    break;
            }

            this.props.context.request.cookies.set(this.props.context.app.config.cookieName, value, cookieOptions);
        }
    }

    private setUserCatalogIdQueryParamInUrl() {
        if (Msdyn365.isBrowser) {
            const currentCatalogId = getCatalogId(this.props.context.request);
            if (currentCatalogId > 0) {
                const url = new URL(window.location.href);
                const urlCatalogId = parseInt(url.searchParams.get(EveUserRoles._catalogIdQueryParamKey) || '0', 10);
                if (urlCatalogId !== currentCatalogId) {
                    url.searchParams.set(EveUserRoles._catalogIdQueryParamKey, currentCatalogId.toString());
                    window.history.replaceState({}, '', `${url.pathname}${url.search}`);
                }
            }
        }
    }

    public async getUserLevel() {
        const userCookie = this.props.context.request.cookies.get(EveUserRoles.USER_COOKIE_NAME_CONST);
        if (
            this.props.app.config.enableUserRoles &&
            //this.props.context.request.user.isAuthenticated &&
            this.props.context.request.cookies.isConsentGiven() &&
            //@ts-ignore ITS REAL I PROMISE
            this.props.context.request.User
        ) {
            //const levelCookie = this.props.context.request.cookies.get(this.props.context.app.config.cookieName);

            const isAuthenticated = this.props.context.request.user.isAuthenticated;
            if (
                isAuthenticated &&
                (!userCookie.value || userCookie.value !== this.props.context.request.user.customerAccountNumber) &&
                //@ts-ignore We need the channelid from the user
                this.props.context.request.User.ChannelId === this.props.context.request.channel.RecordId.toString()
            ) {
                const customerInfo = await getUserLevelByUserIdAsync(
                    {
                        callerContext: this.props.context.actionContext
                    },
                    //@ts-ignore ITS REAL IF I BELIEVE!
                    this.props.context.request.User.ObjectIdentifier
                );
                //What user level does this user have?
                if (customerInfo) {
                    this.setUserRoleCookie(true, true, customerInfo.userLevel);
                    if (customerInfo.userLevel > 0) {
                        this.setUserCatalogIdQueryParamInUrl();
                        window.location.reload();
                    }
                }
            } else if (
                (!isAuthenticated && userCookie.value !== 1) ||
                //@ts-ignore We need the channelid from the user
                (isAuthenticated &&
                    //@ts-ignore
                    this.props.context.request.User &&
                    //@ts-ignore We need the channelid from the user
                    this.props.context.request.User.ChannelId !== this.props.context.request.channel.RecordId.toString())
            ) {
                const refresh = isAuthenticated && userCookie.value !== '1';
                this.setUserRoleCookie(true, true, 0);
                if (refresh) {
                    this.setUserCatalogIdQueryParamInUrl();
                    window.location.reload();
                }
            }
        }
    }

    public static UserRolePermissionsHelper(cookieValue: string, permission: UserRolePermissions): boolean {
        let permissionLevel = 0;
        if (cookieValue === EveUserRoles.lvl1String) {
            permissionLevel = 1;
        } else if (cookieValue === EveUserRoles.lvl2String) {
            permissionLevel = 2;
        } else if (cookieValue === EveUserRoles.lvl21String) {
            permissionLevel = 2.1;
        } else if (cookieValue === EveUserRoles.lvl3String) {
            permissionLevel = 3;
        } else if (cookieValue === EveUserRoles.lvl4String) {
            permissionLevel = 4;
        } else if (cookieValue === EveUserRoles.lvl41String) {
            permissionLevel = 4.1;
        }

        switch (permission) {
            case UserRolePermissions.CanViewProducts:
                if (permissionLevel >= 1) {
                    return true;
                }
                return false;
            case UserRolePermissions.CanViewProductAvailability:
                if (permissionLevel >= 2) {
                    return true;
                }
                return false;
            case UserRolePermissions.CanViewProductPrice:
                if (permissionLevel >= 2.1) {
                    return true;
                }
                return false;
            case UserRolePermissions.CanViewProductPriceInCheckout:
                if (permissionLevel >= 3) {
                    return true;
                }
                return false;
            case UserRolePermissions.CanViewProductBasePrice:
                if (permissionLevel >= 3) {
                    return true;
                }
                return false;
            case UserRolePermissions.CanUseOrderTemplates:
                if (permissionLevel >= 2) {
                    return true;
                }
                return false;
            case UserRolePermissions.CanSubmitOrders:
                if (permissionLevel === 3 || permissionLevel === 4) {
                    return true;
                }
                return false;
            case UserRolePermissions.CanViewInvoices:
                if (permissionLevel >= 4) {
                    return true;
                }
                return false;
        }
    }

    public render(): JSX.Element | null {
        this.getUserLevel();
        return this.props.renderView(this.props);
    }
}

export default EveUserRoles;
